<template>
  <div class="company-pool">
    <div class="company-selector card-panel">
      <div class="company-selector__field">
        <label class="field-label"><i class="el-icon-office-building"></i> 选择入驻公司</label>
        <el-select
          v-model="selectedCompany"
          filterable
          placeholder="请选择公司"
          size="medium"
          style="width: 100%"
          @change="onCompanyChange"
        >
          <el-option
            v-for="co in companyList"
            :key="co.id"
            :label="`${co.companyName}（${co.type}）`"
            :value="co.id"
          ></el-option>
        </el-select>
      </div>
    </div>

    <div class="card-panel" v-if="currentCompany">
      <div class="company-stats-header">
        <div class="company-stats-header__left">
          <h2 class="company-name">{{ currentCompany.companyName }}</h2>
          <el-tag :type="companyTypeTag" size="small">{{ currentCompany.type }} </el-tag>
        </div>
        <div class="company-stats-header__right">
          <div class="stat-item">
            <span class="stat-label">总标签数：</span>
            <b class="stat-value">{{ totalTags }}</b>
          </div>
          <div class="stat-item">
            <span class="stat-label">来自通用池：</span>
            <b class="stat-value stat-value--green">{{ generalCount }}</b>
          </div>
          <div class="stat-item">
            <span class="stat-label">自有创建：</span>
            <b class="stat-value">{{ ownCount }}</b>
          </div>
        </div>
      </div>
      <div class="company-actions">
        <el-button type="success" icon="el-icon-download" @click="openPickGeneral">从通用池选用</el-button>
        <el-button type="success" plain icon="el-icon-plus" @click="openAddOwnTag">新增自有标签</el-button>
      </div>

      <div class="pool-toolbar">
        <div class="pool-toolbar__left">
          <el-input
            v-model="searchKeyword"
            :placeholder="sortMode ? '排序模式下展示全部标签' : '搜索业务池内的标签...'"
            prefix-icon="el-icon-search"
            clearable
            size="medium"
            :disabled="sortMode"
            style="width: 300px"
          ></el-input>
          <span class="source-count">共 {{ filteredTags.length }} 个标签</span>
        </div>
        <div class="pool-toolbar__right">
          <div class="source-filter-btns">
            <button
              v-for="sf in sourceFilters"
              :key="sf.key"
              class="source-filter-btn"
              :class="{ 'source-filter-btn--active': sourceFilter === sf.key }"
              :disabled="sortMode"
              @click="sourceFilter = sf.key"
            >{{ sf.label }}</button>
          </div>
          <span class="sort-label">排序模式</span>
          <el-switch v-model="sortMode"></el-switch>
        </div>
      </div>

      <div v-if="sortMode" class="sort-hint">
        <span>正在调整公司自有标签优先级...</span>
        <div>
          <el-button size="mini" @click="reorderAll">一键重排</el-button>
          <el-button size="mini" type="success" @click="saveSort">保存</el-button>
        </div>
      </div>

      <div class="pool-table-header">
        <div class="pool-table-header__col pool-table-header__col--30">
          <span class="pool-table-header__rank">序号</span>
          <span>标签名称 / 优先级 / 来源 / 主标签</span>
        </div>
        <div class="pool-table-header__col pool-table-header__col--15">策略配置</div>
        <div class="pool-table-header__col pool-table-header__col--30">业务类型 / 状态 / 案件状态 / 联系状态</div>
        <div class="pool-table-header__col pool-table-header__col--25 pool-table-header__col--right">ID / 标签描述 / 操作</div>
      </div>

      <div class="pool-list">
        <tag-card
          v-for="(tag, idx) in filteredTags"
          :key="tag.id"
          :tag="tag"
          :index="idx"
          :pool-type="'company'"
          :sort-mode="sortMode"
          :parent-tag-name="getParentName(tag.parentTagId)"
          :strategy-configured="getStrategyCount(tag)"
          :strategy-total="strategyGroupTotal"
          @edit="openEditDialog"
          @delete="deleteTag"
          @open-strategy="openStrategy"
          @move="moveTag"
          @jump-to="jumpTo"
          @drag-drop="onDragDrop"
        ></tag-card>
      </div>
    </div>

    <tag-form-dialog
      ref="tagFormDialog"
      :pool-type="isOwnMode ? 'company-own' : 'company'"
      :all-tags="allCompanyTags"
      :edit-tag="currentEditTag"
      :selected-company="selectedCompany"
      :filtered-tags="filteredTags"
      :company-business-type="currentCompany ? currentCompany.businessType: ''"
      :case-status-option="caseStatusOption"
      :contact-status-option="contactStatusOption"
      @confirm="onFormConfirm"
    ></tag-form-dialog>

    <pick-general-dialog
      ref="pickGeneralDialog"
      :general-tags="companyPoolTags"
      :company-type="currentCompany ? currentCompany.type : ''"
      :company-business-type="currentCompany ? currentCompany.businessType: ''"
      :configured-ids="configuredGeneralIds"
      :all-company-tags="allCompanyTags"
      @confirm="onPickCompanyConfirm"
    ></pick-general-dialog>

    <strategy-dialog
      ref="strategyDialog"
      :company-type="currentCompany ? currentCompany.type : ''"
      :strategy-config="strategyConfig"
      @toggle-strategy="onToggleStrategy"
    ></strategy-dialog>
  </div>
</template>

<script>
import TagCard from './tag-card';
import TagFormDialog from './tag-form-dialog';
import PickGeneralDialog from './pick-general-dialog';
import StrategyDialog from './strategy-dialog';
import TagPoolMixin from './tag-pool-mixin';
import { getBusinessTypeLabel } from '../../../utils/business-type';

// 最近一次选中的公司 id 缓存键：重新进入公司标签池时用于恢复选择
const COMPANY_CACHE_KEY = 'tagPoolCompanyId';

export default {
  name: 'CompanyPool',
  components: { TagCard, TagFormDialog, PickGeneralDialog, StrategyDialog },
  // 复用标签池公共逻辑（删除标签、上下文校验）
  mixins: [TagPoolMixin],
  props: {
    companyTags: { type: Object, default: () => ({}) },
    companyList: { type: Array, default: () => [] },
    strategyConfig: { type: Object, default: () => ({}) },
    // 是否处于公司标签池 tab（父级点击该 tab 时置为 true）
    active: { type: Boolean, default: false },
    // 父级记录的最近一次选中的公司 id（切换 tab 后不丢失）
    savedCompany: { type: [Number, String], default: null },
    caseStatusOption: { type: Array, default: () => [] },
    contactStatusOption: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectedCompany: null,
      searchKeyword: '',
      sourceFilter: 'all',
      // 已进入公司标签池但公司列表尚未返回，待列表就绪后补执行一次
      pendingEnter: false,
      sourceFilters: [
        { key: 'all', label: '全部' },
        { key: 'general', label: '来自通用池' },
        { key: 'own', label: '公司自有' }
      ],
      sortMode: false,
      // 进入页面时清除排序模式，此期间的 sortMode 变化不再触发重新拉取
      sortModeResetting: false,
      currentEditTag: null,
      isOwnMode: false,
      // 弹窗数据源：每次点开时按当前公司实时拉取
      companyPoolTags: [],
      pickLoading: false,
      // 排序保存中（防重复提交 + 按钮 loading）
      sortSaving: false,
      // 策略组配置请求中（防重复提交）
      strategyToggling: false,
      // 策略组总数（卡片分母）：/strategy/selectAllGroupsByCompanyIdOfUser 返回的元素个数
      strategyGroupTotal: 0,
      // 本地勾选/取消产生的配置数增量：列表里的 strategyConfiguredCount 是接口返回的初始配置数，
      // 本地改动用增量修正；列表重新拉取后增量已被接口数据包含，会被清零
      strategyDelta: {},
      // 策略组总数拉取批次号：新批次开始时旧批次结果自动作废
      strategyGroupToken: 0
    };
  },
  watch: {
    // 每次点击「公司标签池」tab（active 变为 true）都重新进入：
    // 重置筛选条件 -> 恢复上次选中的公司（无记录时选第一家）-> 刷新数据
    active: {
      immediate: true,
      handler(val) {
        if (val) this.enterCompany();
      }
    },
    // 兜底：已进入公司标签池但公司列表接口返回较晚，列表就绪后补执行一次
    companyList() {
      if (this.active && this.pendingEnter) this.enterCompany();
    },
    // 记住最近一次选中的公司 id，页面重新进入（含刷新）时用于恢复选择
    selectedCompany(val) {
      if (val === null || val === undefined || val === '') return;
      localStorage.setItem(COMPANY_CACHE_KEY, String(val));
      // 换公司后旧公司的策略组总数与本地增量都失效，清空重新拉取
      this.resetStrategyOverview();
    },
    // 父级公司标签刷新（切换公司、增删标签、排序保存）后重新取策略组总数：
    // 父级是给 tagData.company.general/own 赋值，props 引用不变，必须深度监听
    companyTags: {
      deep: true,
      handler() {
        // 列表已重新拉取，strategyConfiguredCount 已是最新值，本地增量随之作废
        this.strategyDelta = {};
        this.loadStrategyGroupTotal();
      }
    },
    // 进入排序模式时重置搜索与来源筛选，保证排序针对全量数据
    sortMode(val) {
      if (val) {
        this.sourceFilter = 'all';
        this.searchKeyword = '';
        return;
      }
      // 进入页面时只是清除排序模式，数据由进入流程统一刷新，避免重复请求与“序号已恢复”提示
      if (this.sortModeResetting) return;
      this.reorderAll();
    }
  },
  computed: {
    currentCompany() {
      return this.companyList.find(c => c.id === this.selectedCompany) || null;
    },
    companyTypeTag() {
      const type = this.currentCompany ? this.currentCompany.type : '';
      if (type === '催收类') return '';
      if (type === '保险类') return 'danger';
      if (type === '营销类') return 'warning';
      return 'info';
    },
    allCompanyTags() {
      const list = [...(this.companyTags.general || []), ...(this.companyTags.own || [])];
      return list
        .map(t => ({
          ...t,
          source: Number(t.isCommon) === 0 ? '公司自有标签' : '来自通用标签池'
        }))
        .sort((a, b) => a.priority - b.priority);
    },
    filteredTags() {
      // 排序模式下必须展示全部数据：搜索与来源筛选均不生效
      if (this.sortMode) return this.allCompanyTags;
      let list = this.allCompanyTags;
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase();
        list = list.filter(t =>
          t.name.toLowerCase().includes(kw) ||
          t.code.toLowerCase().includes(kw)
        );
      }
      if (this.sourceFilter === 'general') {
        list = list.filter(t => t.source === '来自通用标签池');
      } else if (this.sourceFilter === 'own') {
        list = list.filter(t => t.source === '公司自有标签');
      }
      return list;
    },
    totalTags() {
      return this.allCompanyTags.length;
    },
    generalCount() {
      return (this.companyTags.general || []).length;
    },
    ownCount() {
      return (this.companyTags.own || []).length;
    },
    configuredGeneralIds() {
      return (this.allCompanyTags || []).map(t => t.detailId);
    }
  },
  methods: {
    getParentName(parentId) {
      if (!parentId) return null;
      const parent = this.allCompanyTags.find(t => t.id === parentId);
      return parent ? parent.name : null;
    },
    // 已配置策略组个数：列表接口返回的初始配置数 strategyConfiguredCount
    // 叠加本地勾选/取消产生的增量，结果不小于 0
    getStrategyCount(tag) {
      const configured = Number(tag.strategyConfiguredCount) || 0;
      const total = configured + (this.strategyDelta[tag.id] || 0);
      return total > 0 ? total : 0;
    },
    resetStrategyOverview() {
      this.strategyGroupTotal = 0;
      this.strategyDelta = {};
    },
    // 策略组总数：/strategy/selectAllGroupsByCompanyIdOfUser 返回列表的元素个数
    loadStrategyGroupTotal() {
      const companyId = this.selectedCompany;
      if (companyId === null || companyId === undefined || companyId === '') return;
      // 每次调用开启新批次：切公司或数据再次刷新时，上一批次的结果自动作废
      this.strategyGroupToken += 1;
      const token = this.strategyGroupToken;
      const isStale = () => this.selectedCompany !== companyId || this.strategyGroupToken !== token;
      this.$ajax({
        url: '/strategy/selectAllGroupsByCompanyIdOfUser',
        method: 'POST',
        data: { companyId }
      })
        .then((res) => {
          if (isStale()) return;
          const groups = Array.isArray(res.data) ? res.data : ((res.data && res.data.list) || []);
          this.strategyGroupTotal = groups.length;
        })
        .catch(() => {
          // 错误提示已由 $ajax 响应拦截器统一弹出，这里保持 0，卡片按未配置展示
        });
    },
    // 静默清除排序模式（进入页面、离开页面、保存排序后）：
    // 不走 reorderAll 的重新拉取与“序号已恢复”提示，数据由调用方负责刷新
    clearSortMode() {
      if (!this.sortMode) return;
      this.sortModeResetting = true;
      this.sortMode = false;
      // sortMode 监听在 nextTick 才执行，等它跑完后再复位标记
      this.$nextTick(() => {
        this.sortModeResetting = false;
      });
    },
    // 进入公司标签池（首次进入或再次进入）时执行：
    // 清除排序模式 -> 重置筛选条件 -> 恢复上次选中的公司（无记录时选第一家）-> 通知父级刷新数据
    enterCompany() {
      // 每次进入都退出排序模式，避免带着上次的排序状态进入
      this.clearSortMode();
      // 每次进入都重置搜索与来源筛选，避免沿用上次的条件
      this.searchKeyword = '';
      this.sourceFilter = 'all';
      const list = this.companyList;
      // 公司列表还没返回：标记待执行，等 companyList 就绪后自动补上
      if (!list || !list.length) {
        this.pendingEnter = true;
        return;
      }
      this.pendingEnter = false;
      // 优先用父级记录的已选公司，页面刷新后父级无记录则回退到本地缓存
      const saved = this.savedCompany !== null && this.savedCompany !== undefined && this.savedCompany !== ''
        ? this.savedCompany
        : localStorage.getItem(COMPANY_CACHE_KEY);
      // 缓存的公司可能已被删除/不在列表中，此时回退到第一家
      let target = null;
      if (saved !== null && saved !== undefined && saved !== '') {
        target = list.find(c => String(c.id) === String(saved)) || null;
      }
      if (!target) target = list[0];
      this.selectedCompany = target.id;
      this.onCompanyChange();
    },
    onCompanyChange() {
      this.sourceFilter = 'all';
      this.$emit('company-change', this.selectedCompany);
      this.$emit('company-change-type', this.currentCompany ? this.currentCompany.type : '');
    },
    openAddOwnTag() {
      this.currentEditTag = null;
      this.isOwnMode = true;
      this.$refs.tagFormDialog.open();
    },
    openEditDialog(tag) {
      this.currentEditTag = tag;
      this.isOwnMode = tag.source === '公司自有标签';
      this.$refs.tagFormDialog.open();
    },
    // 保存成功后的刷新：弹窗已提示，这里只重置编辑态并通知父级按当前公司重新拉取，
    // 保证列表以后端返回为准（本地对象是 computed 生成的副本，直接改不会触发渲染）
    onFormConfirm() {
      this.currentEditTag = null;
      this.onCompanyChange();
    },
    // 公司池的上下文校验：只需选中公司，策略组固定为 0
    validateBatchContext() {
      if (this.selectedCompany == null) return '请先选择归属公司';
      return '';
    },
    // 公司池未按策略组划分，groupId 固定传 0
    getOperationContext() {
      return {
        companyId: this.selectedCompany,
        groupId: 0
      };
    },
    // 删除成功后的刷新：通知父级按当前公司重新拉取公司标签
    onTagDeleted() {
      this.onCompanyChange();
    },
    openPickGeneral() {
      if (this.selectedCompany == null) {
        this.$message.warning('请先选择归属公司');
        return;
      }
      if (this.pickLoading) return;
      this.pickLoading = true;
      this.$ajax({
        url: '/label/list',
        method: 'post',
        data: this.buildLabelListParam('', 0, 0)
      })
        .then((res) => {
          const list = res.data.list || [];
          // 与公司池口径一致：接口暂未返回来源字段，统一按“来自通用池”打标
          this.companyPoolTags = list.map(item => this.normalizeTag(item, 'general'));
          // 显式把最新数据传给弹窗：props 需等下次渲染才更新，直接 open() 会读到旧值
          this.$refs.pickGeneralDialog.open(this.companyPoolTags);
        })
        .catch(() => {
          this.companyPoolTags = [];
          this.$message.error('通用标签加载失败');
        })
        .finally(() => {
          this.pickLoading = false;
        });
    },
    normalizeTag(item, poolType) {
      return {
        ...item,
        id: String(item.id),
        code: item.code || item.labelCode || '',
        type: this.getLabelType(item, poolType),
        desc: item.remark || '',
        parentTagId: item.parentId != null ? String(item.parentId) : null
      };
    },
    getLabelType(tag, poolType) {
      if (poolType === 'company') {
        if (tag.isCommon) {
          return getBusinessTypeLabel(tag.businessType) || '通用类';
        } return this.selectedCompanyType;
      } else if (poolType === 'business') {
        if (tag.isCommon) {
          return getBusinessTypeLabel(tag.businessType) || '通用类';
        } return this.selectedCompanyBusinessType;
      } else if (poolType === 'general') {
        return getBusinessTypeLabel(tag.businessType) || '通用类';
      }
      return '';
    },
    buildLabelListParam(typeFilter, companyId, groupId) {
      return {
        'companyId': companyId,
        'groupId': groupId,
        'label': null,
        'mainStrategy': null,
        'caseStatusList': null,
        'labelStatus': null,
        'businessType': typeFilter,
        'page': 1,
        'limit': 0,
        'order': 'priority',
        'sort': 'asc',
        'isLabelSetGroup': true
      };
    },
    // 批量提交标签到当前公司的标签池，供「从公司池选用」与「排序保存」复用
    // detailIds 的数组顺序即优先级顺序；loadingKey 指定各自按钮的 loading 字段
    // refresh 默认成功后重新拉取公司标签；若 onSuccess 内部已负责刷新（如排序保存）可传 false 避免重复请求
    submitLabelBatch(detailIds, { successMessage, emptyMessage, loadingKey, onSuccess, refresh = true }) {
      if (!this.checkBatchContext()) return;

      const ids = (detailIds || []).filter(id => id !== undefined && id !== null);
      if (!ids.length) {
        this.$message.info(emptyMessage || '没有可提交的标签');
        return;
      }
      if (loadingKey && this[loadingKey]) return;
      if (loadingKey) this[loadingKey] = true;

      this.$ajax({
        url: '/label/addOrEditLabelBatch',
        method: 'POST',
        data: {
          // 目标公司
          companyId: this.selectedCompany,
          // 目标策略组
          groupId: 0,
          // 来源公司（标签取自该公司 groupId=0 的标签池）
          sourceCompanyId: 0,
          // 待提交的标签明细 id 集合
          detailIds: ids
        }
      })
        .then(() => {
          this.$message.success(successMessage);
          if (onSuccess) onSuccess();
          // 通知父级按当前公司重新拉取公司标签
          if (refresh) this.onCompanyChange();
        })
        .catch(() => {})
        .finally(() => {
          if (loadingKey) this[loadingKey] = false;
        });
    },
    // 从公司池选用标签确认：批量新增到当前业务池
    onPickCompanyConfirm(selected) {
      this.submitLabelBatch(
        (selected || []).map(t => t.detailId),
        {
          successMessage: '已添加至公司池',
          emptyMessage: '未选择新的标签',
          loadingKey: 'pickLoading'
        }
      );
    },
    openStrategy(tag) {
      this.$refs.strategyDialog.open(tag);
    },
    // 配置/取消配置策略组：/label/assignedLabelToGroup，入参 LabelOperationReq
    // payload 由 strategy-dialog 给出：{ id, groupId, next }
    // next 为本次操作的目标状态，由弹窗依据「该行当前显示状态」取反得出，
    // 该显示状态已合并本地操作结果与接口返回的 isLabelSet，父级不再自行推断
    onToggleStrategy({ id, groupId, next }) {
      const tag = this.allCompanyTags.find(t => t.id === id);
      if (!tag) {
        this.$message.warning('未找到对应的标签');
        return;
      }
      if (this.selectedCompany == null) {
        this.$message.warning('请先选择归属公司');
        return;
      }
      if (this.strategyToggling) return;
      // 操作前的状态，用于失败回滚（next 取反即为操作前的显示状态）
      const current = !next;
      // 先本地乐观更新，保证弹窗勾选即时响应，失败再回滚
      this.applyStrategyConfig(id, groupId, next);
      this.strategyToggling = true;
      this.$ajax({
        url: '/label/assignedLabelToGroup',
        method: 'POST',
        data: {
          id: tag.id,
          companyId: this.selectedCompany,
          groupId,
          operationType: next ? 'addGroupConfig' : 'deleteGroupConfig'
        }
      })
        .then((res) => {
          this.strategyToggling = false;
          if (!res || res.code !== 200) {
            this.applyStrategyConfig(id, groupId, current);
            return;
          }
          this.$message.success(next ? '已配置策略组' : '已取消策略组配置');
        })
        .catch(() => {
          // 错误提示已由 $ajax 响应拦截器统一弹出，这里只回滚本地配置
          this.strategyToggling = false;
          this.applyStrategyConfig(id, groupId, current);
        });
    },
    // 写入本地策略组配置（strategyConfig 为对象，新增 key 需用 $set 才能触发响应）
    applyStrategyConfig(tagId, groupId, value) {
      if (!this.strategyConfig[tagId]) {
        this.$set(this.strategyConfig, tagId, {});
      }
      this.$set(this.strategyConfig[tagId], groupId, value);
      // 同步已配置个数的增量：勾选 +1、取消 -1；失败回滚时会再写回原值，增量自动抵消
      this.$set(this.strategyDelta, tagId, (this.strategyDelta[tagId] || 0) + (value ? 1 : -1));
    },
    resolveIndex(filteredIndex) {
      const filtered = this.filteredTags;
      if (filteredIndex < 0 || filteredIndex >= filtered.length) return -1;
      const item = filtered[filteredIndex];
      return this.allCompanyTags.findIndex(t => t.id === item.id);
    },
    syncPriorities(list) {
      list.forEach((t, i) => {
        const pri = i + 1;
        const gen = (this.companyTags.general || []).find(g => g.id === t.id);
        if (gen) { gen.priority = pri; return; }
        const own = (this.companyTags.own || []).find(o => o.id === t.id);
        if (own) own.priority = pri;
      });
    },
    moveTag(from, to) {
      const filtered = this.filteredTags;
      const list = this.allCompanyTags;
      if (to < 0) return;

      const actualFrom = this.resolveIndex(from);
      if (actualFrom === -1) return;

      let actualTo;
      if (to === 0) {
        actualTo = 0;
      } else if (to >= filtered.length) {
        actualTo = list.length;
      } else {
        actualTo = this.resolveIndex(to);
        if (actualTo === -1) return;
      }

      const item = list.splice(actualFrom, 1)[0];
      list.splice(actualTo, 0, item);
      this.syncPriorities(list);
    },
    jumpTo(from, target) {
      const filtered = this.filteredTags;
      const targetIdx = Math.min(target, filtered.length - 1);
      if (targetIdx < 0) return;
      this.moveTag(from, targetIdx);
    },
    onDragDrop(from, to) {
      this.moveTag(from, to);
    },
    reorderAll() {
      // 通知父级按当前公司组重新拉取标签
      this.onCompanyChange();
      this.$message.success('序号已恢复');
    },
    // 排序模式下保存：按当前优先级顺序把标签批量提交后端
    saveSort() {
      // 排序模式下 filteredTags 已为全量且按优先级升序
      const list = this.filteredTags;
      // 缺少明细 id 的标签会导致后端顺序不完整，直接阻断避免脏数据
      if (list.some(t => t.detailId === undefined || t.detailId === null)) {
        this.$message.warning('存在缺少明细ID的标签，无法保存排序');
        return;
      }
      this.submitLabelBatch(
        list.map(t => t.detailId),
        {
          successMessage: '排序已保存',
          emptyMessage: '当前没有可保存的标签',
          loadingKey: 'sortSaving',
          // 静默退出排序模式后由 refresh 统一重新拉取，
          // 避免触发 reorderAll 再弹一次“序号已恢复”
          onSuccess: () => {
            this.clearSortMode();
          }
        }
      );
    }
  }
};
</script>

<style lang="less" scoped>
@import '../../../assets/style/variable.less';

.card-panel {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.company-selector {
  &__field {
    max-width: 480px;
  }
}

.field-label {
  font-size: 12px;
  font-weight: bold;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: block;
  margin-bottom: 8px;
}

.company-stats-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 24px;
  }
}

.company-name {
  font-size: 22px;
  font-weight: bold;
  color: #303133;
  margin: 0;
}

.stat-item {
  font-size: 13px;
  color: #909399;
}

.stat-value {
  color: #303133;
  font-size: 14px;

  &--green {
    color: #67c23a;
  }
}

.company-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.pool-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.source-filter-btns {
  display: flex;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 4px;
  border-radius: 8px;
}

.source-filter-btn {
  padding: 6px 16px;
  border-radius: 6px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
  }

  &--active {
    background: #059669;
    color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    &:hover {
      background: #059669;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.source-count {
  font-size: 12px;
  color: #c0c4cc;
}

.sort-label {
  font-size: 12px;
  font-weight: bold;
  color: #909399;
}

.sort-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  padding: 8px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #67c23a;
}

.pool-table-header {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 12px;
  font-weight: bold;
  color: #64748b;
  margin-bottom: 4px;

  &__col {
    display: flex;
    align-items: center;

    &--30 { width: 30%; }
    &--15 { width: 15%; justify-content: center; }
    &--25 { width: 25%; }
    &--right { justify-content: flex-end; }
  }

  &__rank {
    width: 64px;
    flex-shrink: 0;
  }
}

.pool-list {
  min-height: 100px;
}
</style>



// WEBPACK FOOTER //
// src/page/main/tagpools/company-pool.vue